import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import type { Attachment } from '@runloop/reflex-contract';
import type { AgentActivity, AxonEvent } from '@reflex/shared';

/**
 * When a provider is driving an interactive replay, this describes the
 * action the viewer must perform next. Consumers can use it to draw a
 * hint anchored to the matching button.
 */
export interface PendingReplayAction {
  kind: 'send' | 'complete' | 'stop' | 'interrupt';
}

export interface PendingReplayIntro {
  title?: string;
  body: string;
  autoDismissMs?: number;
  interactive: boolean;
}

export interface PendingReplayAnnotation {
  id: string;
  title?: string;
  body: string;
  autoDismissMs?: number;
  interactive: boolean;
  candidateEventIds?: string[];
}

/**
 * Optimistic user message tracked locally between a send request and the
 * corresponding user-message echo arriving in the agent stream.
 */
export interface PendingUserMessage {
  clientId: string;
  text: string;
  attachments?: Array<{ name?: string; mimeType?: string; data?: string }>;
  createdAt: number;
  status: 'sending' | 'unconfirmed' | 'failed';
  error?: string;
  httpAcked?: boolean;
  /**
   * Mailbox command id returned when the send was accepted but its
   * delivery outcome was still unknown (HTTP 202) — typically a sleeping
   * agent being woken. The client watches this command so a delivery
   * failure surfaces on the bubble with a reason instead of waiting out
   * the echo deadline. Persisted so a refresh can resume the watch.
   */
  commandId?: string;
  synthetic?: boolean;
  /**
   * Id of the user the bubble should attribute to. Set for synthetic
   * entries derived from `agent.prompt` so the bubble shows the user who
   * launched the agent (which may be different from the current viewer,
   * e.g. an agent kicked off by a teammate, a scheduled run, or a flow).
   * Omitted for real sends — those default to the current viewer.
   */
  userId?: string;
}

export interface QueuedMessage {
  id: string;
  text: string;
  message?: string;
  content?: unknown;
  createdAt?: number;
  [key: string]: unknown;
}

export interface AgentStreamData {
  events: AxonEvent[] | undefined;
  activity: AgentActivity;
  isWorking: boolean;
  /** Live turn or pending prompt, excluding background-only work. Defaults to isWorking in replays. */
  isTurnWorking?: boolean;
  chatText?: string | null;
  send: (msg: string, attachments: Attachment[]) => void;
  /**
   * Redirect a running agent: interrupt the in-flight turn and send the
   * message immediately, instead of `send` which queues it while the agent is
   * working. Optional so replay/other providers that can't redirect may omit
   * it; the composer only surfaces the redirect affordance when present.
   */
  redirect?: (msg: string, attachments: Attachment[]) => void;
  interrupt: () => void;
  stop: () => void;
  complete: () => void;
  /**
   * Set while replaying a recorded session. Unlike `chatText`, it stays stable
   * through playback, so gate mutations on it. Live providers omit it.
   */
  replay?: boolean;
  hideActions?: boolean;
  queue: QueuedMessage[];
  enqueue: (text: string, attachments?: Attachment[]) => void;
  updateMessage: (id: string, text: string) => void;
  removeMessage: (id: string) => void;
  reorderQueue: (orderedIds: string[]) => void;
  /**
   * Send a queued message immediately by redirecting the running agent:
   * remove it from the queue, interrupt the in-flight turn, and deliver it
   * now instead of waiting for the queue to drain. Optional and depends on
   * {@link redirect}; providers that can't redirect (replay) omit it and the
   * queued-message rows hide the "send now" affordance.
   */
  sendQueuedNow?: (id: string) => void;
  pendingMessages?: PendingUserMessage[];
  retryPending?: (clientId: string) => void;
  dismissPending?: (clientId: string) => void;
  pendingReplayAction?: PendingReplayAction | null;
  pendingReplayAnnotation?: PendingReplayAnnotation | null;
  dismissReplayAnnotation?: () => void;
  replayIntro?: PendingReplayIntro | null;
  dismissReplayIntro?: () => void;
}

const defaultAgentStreamData: AgentStreamData = {
  events: undefined,
  activity: 'idle',
  isWorking: false,
  send: () => undefined,
  complete: () => undefined,
  stop: () => undefined,
  interrupt: () => undefined,
  queue: [],
  enqueue: () => undefined,
  updateMessage: () => undefined,
  removeMessage: () => undefined,
  reorderQueue: () => undefined,
  dismissReplayAnnotation: () => undefined,
  dismissReplayIntro: () => undefined,
};

export const AgentStreamDataContext = createContext<AgentStreamData | null>(null);

export function useAgentStreamData(): AgentStreamData {
  const ctx = useContext(AgentStreamDataContext);
  if (!ctx) {
    throw new Error('useAgentStreamData must be used within an AgentStreamDataProvider');
  }
  return ctx;
}

export function AgentStreamDataProvider({
  value,
  children,
}: {
  value?: Partial<AgentStreamData>;
  children?: ReactNode;
}) {
  return (
    <AgentStreamDataContext.Provider value={{ ...defaultAgentStreamData, ...value }}>
      {children}
    </AgentStreamDataContext.Provider>
  );
}
